import React, { useState } from 'react';
import { X, CaretDown, UserPlus } from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import { alumniService } from '../../../services/alumni.service';
import { toast } from 'sonner';
import { PROFILE_TARGET_FIELDS } from './ImportDataModal';

interface AddDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddDataModal({ isOpen, onClose, onSuccess }: AddDataModalProps) {
  const [isAlumni, setIsAlumni] = useState(true);
  const [donorId, setDonorId] = useState('');
  const [currentName, setCurrentName] = useState('');
  const [designation, setDesignation] = useState('');
  const [company, setCompany] = useState('');
  const [workCountry, setWorkCountry] = useState('');
  const [optionalData, setOptionalData] = useState<Record<string, string>>({});
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setIsAlumni(true);
      setDonorId('');
      setCurrentName('');
      setDesignation('');
      setCompany('');
      setWorkCountry('');
      setOptionalData({});
      setShowOptional(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAlumni && !donorId.trim()) {
      toast.error('Alumni/CSR Id is required for alumni.');
      return;
    }
    if (!currentName.trim()) {
      toast.error('Current Name is required.');
      return;
    }
    if (!designation.trim()) {
      toast.error('Designation is required.');
      return;
    }
    if (!company.trim()) {
      toast.error('Company is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const source_info = {
        current_name: currentName,
        designation,
        company,
        work_country: workCountry,
        ...optionalData
      };
      
      const payload = {
        is_alumni: isAlumni,
        donor_id: isAlumni ? donorId.trim() : undefined,
        source_info
      };

      const res = await alumniService.createAlumniProfile(payload);
      if (res.status === 'success') {
        toast.success(`Profile created successfully! ID: ${res.donor_id}`);
        onSuccess?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to create profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col font-sans animate-in zoom-in-95 duration-200 max-h-[90vh] transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent">
              <UserPlus size={18} weight="bold" />
            </div>
            <div>
              <h2 className="text-sm font-outfit font-bold text-slate-800">Add Profile Data</h2>
              <p className="text-[10px] font-semibold text-slate-400 mt-0.5">Manually create a new profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="p-6 overflow-y-auto flex-1">
          <form id="add-data-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Type Toggle */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  name="type"
                  checked={isAlumni}
                  onChange={() => setIsAlumni(true)}
                  className="text-accent focus:ring-accent"
                />
                Alumni
              </label>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  name="type"
                  checked={!isAlumni}
                  onChange={() => setIsAlumni(false)}
                  className="text-accent focus:ring-accent"
                />
                Non-Alumni
              </label>
            </div>

            {/* Mandatory Fields */}
            <div className="space-y-4 bg-slate-50 border border-slate-100 p-4 rounded-xl">
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-2">Required Fields</h3>
              
              {isAlumni && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Alumni/CSR Id <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={donorId}
                    onChange={(e) => setDonorId(e.target.value)}
                    placeholder="e.g. AA000001"
                    className="w-full text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
                    required
                  />
                </div>
              )}
              
              {!isAlumni && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Donor Id <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value="Auto-generated (e.g. Z-000001)"
                    disabled
                    className="w-full text-xs bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-slate-500 cursor-not-allowed"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Non-Alumni IDs are automatically generated to prevent duplicates.</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Current Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={currentName}
                  onChange={(e) => setCurrentName(e.target.value)}
                  placeholder="Full Name"
                  className="w-full text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Designation <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  placeholder="e.g. Software Engineer"
                  className="w-full text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Company <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Google"
                  className="w-full text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Work Country
                </label>
                <input
                  type="text"
                  value={workCountry}
                  onChange={(e) => setWorkCountry(e.target.value)}
                  placeholder="e.g. India"
                  className="w-full text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
                />
              </div>
            </div>

            {/* Optional Fields Accordion */}
            <div>
              <button
                type="button"
                onClick={() => setShowOptional(!showOptional)}
                className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-accent transition-colors py-2"
              >
                <CaretDown
                  size={14}
                  weight="bold"
                  className={`transition-transform duration-200 ${showOptional ? 'rotate-180' : ''}`}
                />
                {showOptional ? 'Hide' : 'Show'} Optional Fields
              </button>
              
              {showOptional && (
                <div className="mt-3 space-y-3 p-4 border border-slate-100 rounded-xl animate-in slide-in-from-top-2 fade-in duration-200 max-h-[40vh] overflow-y-auto">
                  {PROFILE_TARGET_FIELDS.filter(f => !['donor_id', 'current_name', 'is_alumni', 'designation', 'company', 'work_country'].includes(f.key)).map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-bold text-slate-600 mb-1">
                        {field.label.replace('*', '')}
                      </label>
                      <input
                        type="text"
                        value={optionalData[field.key] || ''}
                        onChange={(e) => setOptionalData({ ...optionalData, [field.key]: e.target.value })}
                        placeholder={`Enter ${field.label.replace('*', '').toLowerCase()}`}
                        className="w-full text-xs bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-smooth"
                      />
                    </div>
                  ))}
                  <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
                    Note: Other fields can be updated via the bulk import tool by admins.
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* ── Footer ── */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" form="add-data-form" disabled={isSubmitting} className="shadow-sm shadow-accent/20">
            {isSubmitting ? 'Creating...' : 'Create Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
}
